/** @forge-header
 * @id          nflParsers
 * @role        NFL ticker parsers | game, player props (stat ladders + touchdown binaries), super bowl
 * @pattern     pure-fn
 * @purity      none
 * @units       n/a
 * @upstream    kalshiTicker/types:ParsedTicker,GameInfo,PlayerInfo,TickerCategory | kalshiTicker/helpers:nflTeamFromCode,parseFallback,formatPlayerName,splitCompetitors | kalshiTicker/teamNames:NFL_TEAM_CODES,splitNflTeams
 * @downstream  kalshiTicker/parse | stores/marketStore (classifyNflExtendedTicker) | lib/sport/propStat
 * @exports     parseNflGame:fn | parseNflExtended:fn | parseSuperBowl:fn | classifyNflExtendedTicker:fn | isNflExtendedSeriesTicker:fn | nflPropRungLabels:fn | NFL_PROP_SERIES | NFL_PROP_STATS | NFL_PROP_STAT_LABELS | NFL_PROP_CATEGORIES
 * @test        parse.test.ts | nfl.props.test.ts
 */

import type { ParsedTicker, GameInfo, PlayerInfo, TickerCategory } from '../types';
import { parseDateFromTicker, formatDateShort } from '../types';
import { parseFallback, nflTeamFromCode, formatPlayerName, splitCompetitors } from '../helpers';
import { NFL_TEAM_CODES, splitNflTeams } from '../teamNames';

// ── NFL per-game player props ──────────────────────────────────────────
//
// Kalshi's per-game player series (hydracore nfl-player-props, live on he5
// since 2026-09-03). Two shapes, both on the KXNFLGAME event core
// `<YY><MON><DD><AWAY><HOME>` (no HHMM block, no doubleheader suffix):
//
//   Stat ladders (yes-ladder, floor_strike = N − 0.5, YES at N or more):
//     <series>-<core>-<TEAM><INITIAL><SURNAME><JERSEY>-<N>
//     e.g. KXNFLPASSYDS-26SEP10SFLAR-SFBPURDY13-350   "Brock Purdy: 350+"
//   Touchdown binaries (structured yes/no, NO threshold segment):
//     <series>-<core>-<TEAM><INITIAL><SURNAME><JERSEY>   KXNFLFIRSTTD-…-SEASDARNOLD14
//     <series>-<core>-NONE                                "No Touchdown" leg
//     KXNFLDSTTD-<core>-Y                                 per-game "D/ST touchdown"
//
// The team code is 2–3 letters (GB KC LV NE NO SF TB are 2), so the player
// segment is split on a KNOWN code — the event pair first, the registry
// second — never a fixed offset (`SFBPURDY13` is SF · B · PURDY · 13, not
// SFB · PURDY). Same rule as the event pair itself (`splitNflTeams`).
//
// The wire carries `class: 'player-prop'` + `stat` on every row (core stamps
// them; edges forward them), and the stat ids below are the WIRE values
// verbatim. marketStore prefers those and uses this classifier as the
// ticker-grammar fallback / identity source for the player segment.

/** The NFL player-prop stat families — hydracore's `stat` vocabulary, verbatim. */
export type NflPropStat =
  | 'pass-yds' | 'pass-td' | 'pass-att' | 'pass-comp' | 'pass-int'
  | 'rec-yds' | 'rec' | 'long-rec'
  | 'rush-yds' | 'rush-att' | 'long-rush' | 'rush-rec-yds'
  | 'first-td' | 'anytime-td' | 'multi-td' | 'dst-td'
  /** KXNFLTD — the per-player touchdown yes-ladder ("Jordan James: 1+");
   *  core ships it as `player-prop` / `td` (nfl-game-lines-props, pending). */
  | 'td'
  | 'tackles' | 'sacks' | 'int';

/** Series ticker → stat. Keyed by the EXACT series segment (before the first
 *  dash) — a startsWith match would let KXNFLREC eat KXNFLRECYDS. */
export const NFL_PROP_SERIES: Record<string, NflPropStat> = {
  KXNFLPASSYDS: 'pass-yds',
  KXNFLPASSTDS: 'pass-td',
  KXNFLPASSATT: 'pass-att',
  KXNFLPASSCOMP: 'pass-comp',
  KXNFLPASSINT: 'pass-int',
  KXNFLRECYDS: 'rec-yds',
  KXNFLREC: 'rec',
  KXNFLLONGREC: 'long-rec',
  KXNFLRSHYDS: 'rush-yds',
  KXNFLRSHATT: 'rush-att',
  KXNFLLONGRSH: 'long-rush',
  KXNFLRRYDS: 'rush-rec-yds',
  KXNFLFIRSTTD: 'first-td',
  KXNFLANYTD: 'anytime-td',
  KXNFL2TD: 'multi-td',
  KXNFLDSTTD: 'dst-td',
  KXNFLTD: 'td',
  KXNFLTKL: 'tackles',
  KXNFLSACK: 'sacks',
  KXNFLINT: 'int',
};

/** Series tickers longest-first — the order `parse.ts` must register them in
 *  (KXNFLRECYDS before KXNFLREC; KXNFLPASSINT before KXNFLINT). */
export const NFL_PROP_SERIES_LONGEST_FIRST: readonly string[] = Object.keys(NFL_PROP_SERIES)
  .sort((a, b) => b.length - a.length || a.localeCompare(b));

/** Display order for the Player Props stat chips: Kalshi's tab order
 *  (Passing / Receiving / Rushing / Rushing + Receiving / Touchdowns), then
 *  the defensive families. */
export const NFL_PROP_STATS: readonly NflPropStat[] = [
  'pass-yds', 'pass-td', 'pass-att', 'pass-comp', 'pass-int',
  'rec-yds', 'rec', 'long-rec',
  'rush-yds', 'rush-att', 'long-rush',
  'rush-rec-yds',
  'td', 'first-td', 'anytime-td', 'multi-td', 'dst-td',
  'tackles', 'sacks', 'int',
];

export const NFL_PROP_STAT_LABELS: Record<NflPropStat, string> = {
  'pass-yds': 'Passing Yards',
  'pass-td': 'Passing TDs',
  'pass-att': 'Pass Attempts',
  'pass-comp': 'Completions',
  'pass-int': 'Interceptions Thrown',
  'rec-yds': 'Receiving Yards',
  rec: 'Receptions',
  'long-rec': 'Longest Reception',
  'rush-yds': 'Rushing Yards',
  'rush-att': 'Rush Attempts',
  'long-rush': 'Longest Rush',
  'rush-rec-yds': 'Rush + Rec Yards',
  td: 'Touchdowns',
  'first-td': '1st Touchdown',
  'anytime-td': 'Anytime TD',
  'multi-td': '2+ Touchdowns',
  'dst-td': 'D/ST Touchdown',
  tackles: 'Tackles',
  sacks: 'Sacks',
  int: 'Interceptions',
};

/** The touchdown families are structured yes/no markets with NO threshold
 *  segment. The prop pipeline (`PropRung`, the `|prop-…-<threshold>` synth key)
 *  is numeric by contract, so each binary carries a FIXED rung threshold — its
 *  own "N+" reading (anytime = 1+ TD, 2TD = 2+ TDs) — as identity only; the
 *  wording below never prints it. */
export const NFL_PROP_BINARY_THRESHOLD: Partial<Record<NflPropStat, number>> = {
  'first-td': 1,
  'anytime-td': 1,
  'multi-td': 2,
  'dst-td': 1,
};

export function isNflBinaryPropStat(stat: NflPropStat): boolean {
  return NFL_PROP_BINARY_THRESHOLD[stat] !== undefined;
}

/** Reserved player keys for the two team-less legs. `NONE` is Kalshi's own
 *  outcome token; `DST` names the per-game D/ST touchdown yes/no. */
export const NFL_PROP_NONE_PLAYER_KEY = 'NONE';
export const NFL_PROP_DST_PLAYER_KEY = 'DST';
export const NFL_PROP_NONE_PLAYER_NAME = 'No Touchdown';
export const NFL_PROP_DST_PLAYER_NAME = 'D/ST';

export const NFL_PROP_CATEGORIES: Record<NflPropStat, TickerCategory> = {
  'pass-yds': 'nfl-pass-yds',
  'pass-td': 'nfl-pass-td',
  'pass-att': 'nfl-pass-att',
  'pass-comp': 'nfl-pass-comp',
  'pass-int': 'nfl-pass-int',
  'rec-yds': 'nfl-rec-yds',
  rec: 'nfl-rec',
  'long-rec': 'nfl-long-rec',
  'rush-yds': 'nfl-rush-yds',
  'rush-att': 'nfl-rush-att',
  'long-rush': 'nfl-long-rush',
  'rush-rec-yds': 'nfl-rush-rec-yds',
  td: 'nfl-td',
  'first-td': 'nfl-first-td',
  'anytime-td': 'nfl-anytime-td',
  'multi-td': 'nfl-2td',
  'dst-td': 'nfl-dst-td',
  tackles: 'nfl-tackles',
  sacks: 'nfl-sacks',
  int: 'nfl-int',
};

/** True when the ticker's series segment (exact match) is one of the NFL
 *  player-prop series. Cheap pre-gate, like `isMlbExtendedSeriesTicker`. */
export function isNflExtendedSeriesTicker(ticker: string): boolean {
  const i = ticker.indexOf('-');
  const series = (i > 0 ? ticker.slice(0, i) : ticker).toUpperCase();
  return series in NFL_PROP_SERIES;
}

/** Structural classification of one NFL player-prop market ticker. */
export interface NflExtendedClass {
  kind: 'prop';
  stat: NflPropStat;
  /** Event core, for the game label. Null codes when the pair is unknown. */
  date: string;
  awayCode: string | null;
  homeCode: string | null;
  /** Which leg this is: a player's market, the `NONE` ("No Touchdown") leg of
   *  a touchdown series, or the per-game D/ST touchdown yes/no. */
  leg: 'player' | 'none' | 'dst';
  /** '' on the NONE / D/ST legs (no team). */
  teamCode: string;
  /** `<INITIAL><SURNAME>` from the ticker; the reserved `NONE` / `DST` keys
   *  on the team-less legs. */
  playerId: string;
  jerseyNumber?: number;
  /** Yes-ladder threshold (YES resolves at N or more; floor N − 0.5). Null on
   *  the touchdown binaries, which carry no strike. */
  threshold: number | null;
}

/** Split `<TEAM><REST>` on a KNOWN NFL code. The event pair is the authority
 *  when known (the player's team is one of the two by construction — that is
 *  what makes `NE` + `RSTEVENSON38` unambiguous); the full registry is the
 *  fallback, longest code first. Null when no known code leads the blob. */
function splitKnownNflTeamPrefix(
  blob: string,
  pair: readonly [string, string] | null,
): [string, string] | null {
  const candidates = pair ? [...pair].sort((a, b) => b.length - a.length) : [];
  for (const code of candidates) {
    if (blob.startsWith(code) && blob.length > code.length) return [code, blob.slice(code.length)];
  }
  for (const len of [3, 2]) {
    const code = blob.slice(0, len);
    if (code.length === len && NFL_TEAM_CODES.has(code) && blob.length > len) {
      return [code, blob.slice(len)];
    }
  }
  return null;
}

/**
 * Classify an NFL player-prop ticker by its EXACT series segment. Returns null
 * for anything that isn't one of the prop series or whose market segments
 * don't match the published anatomy (fail-safe: callers drop + log rather
 * than guessing — the mlb-props-events rule).
 */
export function classifyNflExtendedTicker(ticker: string): NflExtendedClass | null {
  const raw = ticker.trim().toUpperCase();
  const parts = raw.split('-');
  const series = parts[0] ?? '';
  const stat = NFL_PROP_SERIES[series];
  if (!stat) return null;

  const core = parts[1] ?? '';
  if (core.length < 11) return null; // 7 date chars + the shortest pair (KCGB)
  const date = parseDateFromTicker(core.slice(0, 7));
  if (!date) return null;
  const pair = splitNflTeams(core.slice(7));
  const base = {
    kind: 'prop' as const,
    stat,
    date,
    awayCode: pair?.[0] ?? null,
    homeCode: pair?.[1] ?? null,
  };

  const binary = isNflBinaryPropStat(stat);
  if (binary) {
    // <series>-<core>-<TEAM><PLAYER> | -NONE | -Y (D/ST)
    if (parts.length !== 3) return null;
    const seg = parts[2] ?? '';
    if (stat === 'dst-td') {
      if (seg !== 'Y') return null;
      return { ...base, leg: 'dst', teamCode: '', playerId: NFL_PROP_DST_PLAYER_KEY, threshold: null };
    }
    if (seg === NFL_PROP_NONE_PLAYER_KEY) {
      return { ...base, leg: 'none', teamCode: '', playerId: NFL_PROP_NONE_PLAYER_KEY, threshold: null };
    }
    const player = parsePlayerSegment(seg, pair);
    if (!player) return null;
    return { ...base, leg: 'player', ...player, threshold: null };
  }

  // <series>-<core>-<TEAM><PLAYER>-<N>
  if (parts.length !== 4) return null;
  const player = parsePlayerSegment(parts[2] ?? '', pair);
  if (!player) return null;
  const threshold = /^\d+$/.test(parts[3] ?? '') ? Number(parts[3]) : NaN;
  if (!Number.isFinite(threshold)) return null;
  return { ...base, leg: 'player', ...player, threshold };
}

function parsePlayerSegment(
  seg: string,
  pair: readonly [string, string] | null,
): { teamCode: string; playerId: string; jerseyNumber?: number } | null {
  const split = splitKnownNflTeamPrefix(seg, pair);
  if (!split) return null;
  const m = split[1].match(/^([A-Z]+)(\d+)?$/);
  if (!m) return null;
  return {
    teamCode: split[0],
    playerId: m[1],
    jerseyNumber: m[2] ? Number(m[2]) : undefined,
  };
}

/** The rung, without the player: "350+ Passing Yards"; a touchdown binary
 *  reads as its family name ("1st Touchdown") — the fixed identity threshold
 *  is never printed. */
export function nflPropRungLabel(stat: NflPropStat, threshold: number): string {
  return isNflBinaryPropStat(stat)
    ? NFL_PROP_STAT_LABELS[stat]
    : `${threshold}+ ${NFL_PROP_STAT_LABELS[stat]}`;
}

/** The two sides of ONE NFL prop rung, worded from the same parts so they
 *  cannot drift (the #251 rule `mlbPropRungLabels` set). YES names the
 *  outcome; NO is its complement, repeating the market's own "N+" token.
 *
 *  Touchdown binaries word as events rather than thresholds; the `NONE` leg
 *  (identified by its reserved playerKey, since its "player" is the outcome
 *  itself) is the one row whose YES is the absence of a touchdown. */
export function nflPropRungLabels(
  playerName: string,
  teamCode: string | null | undefined,
  stat: NflPropStat,
  threshold: number,
  playerKey?: string,
): { label: string; notLabel: string } {
  const team = teamCode ? ` (${teamCode})` : '';
  const who = `${playerName}${team}`;
  if (playerKey === NFL_PROP_NONE_PLAYER_KEY) {
    return { label: NFL_PROP_NONE_PLAYER_NAME, notLabel: 'A touchdown is scored' };
  }
  switch (stat) {
    case 'first-td':
      return { label: `${who} scores 1st TD`, notLabel: `${who} does not score 1st TD` };
    case 'anytime-td':
    case 'dst-td':
      return { label: `${who} scores a TD`, notLabel: `${who} does not score a TD` };
    case 'multi-td':
      return { label: `${who} scores 2+ TDs`, notLabel: `${who} does not score 2+ TDs` };
    default: {
      const rung = nflPropRungLabel(stat, threshold);
      return { label: `${who} ${rung}`, notLabel: `${who} does not get ${rung}` };
    }
  }
}

/** A parsed NFL prop's bet, in two parts (#964). `subject` is the player and
 *  rung ("D. Adams #17 11+"); `marketType` is the ladder stat that says what
 *  the rung counts ("Receptions" — not Receiving Yards), or null when the
 *  subject already names it (a touchdown binary, No Touchdown). Kept apart so
 *  a one-line side badge, which may ellipsize its label (#603), can carry the
 *  subject while the stat is shown beside it and never clipped away. */
export function nflPropBetParts(parsed: ParsedTicker): { subject: string; marketType: string | null; threshold?: string } | null {
  const stat = (Object.keys(NFL_PROP_CATEGORIES) as NflPropStat[])
    .find((s) => NFL_PROP_CATEGORIES[s] === parsed.category);
  if (!stat) return null;
  if (parsed.outcome === NFL_PROP_NONE_PLAYER_KEY) return { subject: NFL_PROP_NONE_PLAYER_NAME, marketType: null };
  const who = parsed.player?.displayName ?? parsed.player?.playerId ?? null;
  const ladder = !isNflBinaryPropStat(stat) && !!parsed.outcome && /^\d+\+$/.test(parsed.outcome);
  const rung = ladder ? parsed.outcome! : NFL_PROP_STAT_LABELS[stat];
  return { subject: who ? `${who} ${rung}` : rung, marketType: ladder ? NFL_PROP_STAT_LABELS[stat] : null,
    ...(ladder ? { threshold: rung } : {}) };
}

/** The whole bet as one phrase ("B. Purdy #13 350+ Passing Yards",
 *  "S. Darnold #14 1st Touchdown", "No Touchdown") — `describeMarket.betTeam`,
 *  which text surfaces print whole (My Trades text, the fills ticker, the
 *  edit-price dialog). Side badges show `subject` with the stat beside or
 *  under them instead. The same wording as `nflPropRungLabel`, which the Game
 *  Detail rows and order ticket read. */
export function nflPropBetLabel(parsed: ParsedTicker): string | null {
  const parts = nflPropBetParts(parsed);
  if (!parts) return null;
  return parts.marketType ? `${parts.subject} ${parts.marketType}` : parts.subject;
}

/**
 * Display parser for every NFL player-prop series (dispatched per prefix from
 * parse.ts). Replaces the fixed 3+3/3+2 away/home slice and the 3-letter
 * player-team assumption the first two TD parsers carried — both mis-split
 * every 2-letter team (`SFLAR` → SFL/AR, `SFBPURDY13` → SFB/PURDY).
 */
export function parseNflExtended(raw: string): ParsedTicker {
  const cls = classifyNflExtendedTicker(raw);
  if (!cls) return parseFallback(raw);
  const series = raw.split('-')[0];

  const game: GameInfo | null =
    cls.awayCode && cls.homeCode
      ? {
          awayCode: cls.awayCode,
          homeCode: cls.homeCode,
          awayName: nflTeamFromCode(cls.awayCode) ?? undefined,
          homeName: nflTeamFromCode(cls.homeCode) ?? undefined,
        }
      : null;
  const dateLabel = formatDateShort(cls.date);
  const gameLabel = game ? `${game.awayCode} @ ${game.homeCode}` : '';
  const statLabel = NFL_PROP_STAT_LABELS[cls.stat];

  const player: PlayerInfo | null =
    cls.leg === 'player'
      ? {
          teamCode: cls.teamCode,
          playerId: cls.playerId,
          jerseyNumber: cls.jerseyNumber,
          displayName: formatPlayerName(cls.playerId, cls.jerseyNumber),
        }
      : null;
  const propLabel = cls.threshold != null ? `${statLabel} ${cls.threshold}+` : statLabel;
  const playerLabel =
    cls.leg === 'none' ? NFL_PROP_NONE_PLAYER_NAME
      : cls.leg === 'dst' ? ''
        : player?.displayName ?? player?.playerId ?? '';
  const outcome =
    cls.threshold != null ? `${cls.threshold}+`
      : cls.leg === 'none' ? NFL_PROP_NONE_PLAYER_KEY
        : cls.leg === 'dst' ? 'Y'
          : `${cls.teamCode}${cls.playerId}${cls.jerseyNumber ?? ''}`;

  return {
    raw,
    category: NFL_PROP_CATEGORIES[cls.stat],
    series,
    date: cls.date,
    game,
    player,
    spread: null,
    total: null,
    tennis: null,
    outcome,
    shortLabel: playerLabel ? `${propLabel}: ${playerLabel}` : `NFL ${propLabel}`,
    fullLabel: [`NFL ${propLabel}`, dateLabel, gameLabel, playerLabel]
      .filter(Boolean)
      .join(' · '),
  };
}

/** Super Bowl winner: KXSB-26-SEA */
export function parseSuperBowl(raw: string): ParsedTicker {
  const parts = raw.split('-');
  const year = parts[1] ? `20${parts[1]}` : null;
  const teamCode = parts[2] ?? null;

  const teamName = teamCode ? nflTeamFromCode(teamCode) : null;

  const shortLabel = teamCode ? `SB: ${teamName ?? teamCode}` : 'Super Bowl';

  const fullLabel = year
    ? `Super Bowl ${year}${teamCode ? ` · ${teamName ?? teamCode}` : ''}`
    : shortLabel;

  return {
    raw,
    category: 'nfl-superbowl',
    series: 'KXSB',
    date: null,
    game: null,
    player: null,
    spread: null,
    total: null,
    tennis: null,
    outcome: teamCode,
    shortLabel,
    fullLabel,
  };
}

/**
 * NFL game moneyline: KXNFLGAME-26FEB08SEANE-SEA
 *
 * Team codes are 2 or 3 characters (GB, KC, NE vs DET, CIN, PIT), so the pack
 * has no fixed width to slice at — the away/home split is anchored on the
 * outcome code, as in `parseCfbGame`. An earlier fixed 3+2 slice mis-rendered
 * 52 of 64 live tickers (`DETCIN` → `DET @ CI`, `GBPIT` → `GBP @ IT`) — a
 * confidently wrong label, since the category still resolved (#356).
 *
 * Codes stay exactly as the ticker packs them and are never normalized against
 * hydra's canonical set: Kalshi writes Jacksonville as `JAC` where hydra
 * stamps `JAX`, and rewriting it here would put a second authority on team
 * identity inside a label parser. The name registry carries both spellings so
 * the rendered label is right either way.
 */
export function parseNflGame(raw: string): ParsedTicker {
  const parts = raw.split('-');
  if (parts.length < 2) {
    return parseFallback(raw);
  }

  const mid = parts[1] ?? '';
  // 7 date chars + a 2-char code each side — the shortest real matchup (KCGB).
  if (mid.length < 11) {
    return parseFallback(raw);
  }

  const date = parseDateFromTicker(mid.slice(0, 7));
  const teamsStr = mid.slice(7);

  const teamCode = parts[2] ?? null;
  const [awayCode, homeCode] = splitCompetitors(teamsStr, teamCode);

  const awayName = nflTeamFromCode(awayCode);
  const homeName = nflTeamFromCode(homeCode);

  const game: GameInfo = {
    awayCode,
    homeCode,
    awayName: awayName ?? undefined,
    homeName: homeName ?? undefined,
  };

  const shortLabel = `NFL: ${awayCode} @ ${homeCode}`;
  const fullLabel = teamCode
    ? `NFL: ${awayName ?? awayCode} @ ${homeName ?? homeCode} · ${teamCode} Win`
    : `NFL: ${awayName ?? awayCode} @ ${homeName ?? homeCode}`;

  return {
    raw,
    category: 'nfl-game',
    series: 'KXNFLGAME',
    date,
    game,
    player: null,
    spread: null,
    total: null,
    tennis: null,
    outcome: teamCode,
    shortLabel,
    fullLabel,
  };
}
